import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Shield, Inbox, ListChecks, Calendar, MessageSquare, GraduationCap, Trophy, Tent, Music, Stethoscope, Wallet, FileSignature, Bus, PartyPopper, Check, ChevronDown, Heart, Clock, Sun, CalendarDays } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import { joinWaitlist } from "@/lib/api";
import { LANDING } from "@/constants/testIds";
import { toast } from "sonner";

const CATEGORIES = [
  { icon: GraduationCap, label: "School", tint: "var(--fcc-emma)" },
  { icon: Trophy, label: "Sports", tint: "var(--fcc-peach)" },
  { icon: Tent, label: "Camps", tint: "var(--fcc-noah)" },
  { icon: Music, label: "Activities", tint: "var(--fcc-green-soft)" },
  { icon: Stethoscope, label: "Appointments", tint: "var(--fcc-emma)" },
  { icon: Wallet, label: "Payments", tint: "var(--fcc-peach)" },
  { icon: FileSignature, label: "Forms", tint: "var(--fcc-noah)" },
  { icon: Bus, label: "Transport", tint: "var(--fcc-green-soft)" },
  { icon: PartyPopper, label: "Events", tint: "var(--fcc-emma)" },
];

const V1_PILLARS = [
  { num: "01", icon: Inbox, title: "Child Inbox", body: "We aggregate every email tied to your kids — school, sports, camps, activities — and quietly hide the rest of the noise." },
  { num: "02", icon: Sparkles, title: "AI Extraction", body: "Deadlines, events, payments, forms, gear lists, snack duty, location changes — pulled out of plain-text emails and turned into structured items." },
  { num: "03", icon: CalendarDays, title: "Family Timeline", body: "The killer feature. Today / Tomorrow / This Week. One clean view of every kid, every commitment. Parents don't want emails. Parents want answers." },
  { num: "04", icon: MessageSquare, title: "KidHQ Copilot", body: "Ask in plain English: What is due this week? What forms need signing? Any payments due? What does Emma have tomorrow? Answers in seconds, sourced from your real inbox." },
];

const ROADMAP = [
  { phase: "Phase 2", label: "Inbox integrations", items: ["Gmail OAuth", "Google Calendar", "Outlook"], live: false },
  { phase: "Phase 3", label: "Activity platforms", items: ["TeamSnap", "SportsEngine", "ParentSquare", "ClassDojo", "Remind"], live: false },
  { phase: "Phase 4", label: "Family coordination", items: ["Shared custody", "Grandparents", "Babysitters", "Caregivers"], live: false },
  { phase: "Phase 5", label: "AI Actions", items: ["Register for camps", "Pay fees", "Fill forms", "Add calendar events", "Book appointments"], live: false, highlight: true },
];

const FAQS = [
  { q: "What exactly does KidHQ do?", a: "KidHQ is the AI operating system for parents. It automatically turns school, sports, camp, and activity communications into tasks, deadlines, reminders, and schedules — so you never miss what matters for your kids." },
  { q: "Is this just for school emails?", a: "No. KidHQ aggregates everything tied to your kids: school, sports, camps, extracurriculars, appointments, payments, forms, transportation, and family events. Adult-only emails (newsletters, statements, marketing) are filtered out." },
  { q: "Do I have to connect Gmail to try it?", a: "No. The MVP demo uses realistic sample messages so you can experience the product immediately. Gmail, Outlook, and Google Calendar integrations are coming in Phase 2." },
  { q: "Can KidHQ actually take action for me?", a: "Not yet — but soon. Phase 5 introduces AI Actions: register for camps, pay fees, fill forms, add calendar events, and book appointments — with you in approval-mode at first." },
  { q: "Will my partner / co-parent see this too?", a: "Family coordination (shared custody, grandparents, babysitters, caregivers) ships in Phase 4." },
  { q: "How is my data handled?", a: "Email contents are processed by Claude (Anthropic) over an encrypted channel and never used to train models. KidHQ stores only what's needed to keep your dashboard accurate. You can wipe your data in one click." },
];

const PRICING = [
  { name: "Starter", price: "Free", period: "forever", features: ["1 parent", "Up to 2 kids", "100 emails / month", "Web app"], cta: "Join waitlist", highlight: false, testId: "pricing-starter" },
  { name: "Family", price: "$12", period: "/ month", features: ["2 parents", "Up to 5 kids", "Unlimited emails", "Family timeline", "Daily digest", "Copilot chat"], cta: "Start free trial", highlight: true, testId: LANDING.pricingMonthly },
  { name: "Family Annual", price: "$99", period: "/ year", features: ["Everything in Family", "2 months free", "Priority support", "Early access to AI Actions"], cta: "Save with annual", highlight: false, testId: LANDING.pricingYearly },
];

export default function LandingPage() {
  return (
    <div className="relative overflow-hidden">
      <MarketingHeader />
      <Hero />
      <CategoryStrip />
      <Pillars />
      <Timeline />
      <ProductShot />
      <Pricing />
      <Roadmap />
      <Privacy />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="fcc-pill bg-white border border-[var(--fcc-border)] text-[var(--fcc-green)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--fcc-orange)]" /> The AI Command Center for Parents
          </div>
          <h1 className="font-display text-[44px] leading-[1.02] sm:text-[64px] lg:text-[80px] text-[var(--fcc-ink)] mt-6">
            Never miss another <em className="not-italic text-[var(--fcc-green)]">form, payment, practice, camp, or school deadline.</em>
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-[var(--fcc-ink-soft)] max-w-2xl leading-relaxed">
            KidHQ's AI automatically organizes your <strong className="text-[var(--fcc-ink)]">school emails, sports schedules, camp registrations, forms, payments, and family activities</strong> into one simple dashboard.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#waitlist" data-testid={LANDING.ctaJoinWaitlistHero} className="fcc-btn-primary justify-center">
              Join the waitlist <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/app" data-testid={LANDING.ctaTryDemo} className="fcc-btn-outline justify-center">
              See the live demo
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-4 text-sm text-[var(--fcc-ink-soft)]">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-[var(--fcc-emma)] border-2 border-[var(--fcc-cream)]" />
              <div className="w-8 h-8 rounded-full bg-[var(--fcc-noah)] border-2 border-[var(--fcc-cream)]" />
              <div className="w-8 h-8 rounded-full bg-[var(--fcc-green-soft)] border-2 border-[var(--fcc-cream)]" />
              <div className="w-8 h-8 rounded-full bg-[var(--fcc-peach)] border-2 border-[var(--fcc-cream)]" />
            </div>
            <span>740+ parents on the waitlist · kidhq.net</span>
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <HeroPreview />
        </div>
      </div>
    </section>
  );
}

function HeroPreview() {
  return (
    <div className="relative">
      <div className="fcc-card p-7 anim-fade-up" style={{ animationDelay: ".15s" }}>
        <div className="flex items-center justify-between">
          <span className="fcc-pill fcc-chip-emma">Today · Friday</span>
          <span className="text-xs text-[var(--fcc-muted)]">KidHQ family timeline</span>
        </div>
        <p className="font-display text-[22px] leading-tight text-[var(--fcc-ink)] mt-4">
          Bring Emma's insurance card to her 3:30 dentist appointment.
        </p>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">Pack picnic blanket + water bottles for the school picnic tomorrow.</p>
        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          <Stat label="Action req." value="6" />
          <Stat label="This week" value="10" />
          <Stat label="Detected" value="8" />
        </div>
      </div>
      <div className="fcc-card-accent p-5 mt-6 anim-fade-up" style={{ animationDelay: ".3s" }}>
        <div className="label-overline">Tonight's prep list</div>
        <ul className="mt-3 space-y-2 text-sm text-[var(--fcc-ink)]">
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-0.5" /> Sign Emma's planetarium slip + $12</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-0.5" /> Pack orange slices for Saturday game</li>
          <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-0.5" /> Submit Noah's camp medical form</li>
        </ul>
      </div>
      <div className="absolute -bottom-8 -right-4 w-32 h-32 rounded-full bg-[var(--fcc-peach)] opacity-50 blur-3xl -z-10" />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[var(--fcc-border)] rounded-2xl py-3">
      <div className="font-display text-3xl text-[var(--fcc-ink)]">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-[var(--fcc-muted)] mt-0.5">{label}</div>
    </div>
  );
}

function CategoryStrip() {
  return (
    <section className="py-16 border-y border-[var(--fcc-border)] bg-[var(--fcc-cream-2)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="label-overline text-center">KidHQ understands every channel your kids' lives run through</p>
        <div className="mt-8 grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-4">
          {CATEGORIES.map((c) => (
            <div key={c.label} className="flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: c.tint }}>
                <c.icon className="w-5 h-5 text-[var(--fcc-green)]" />
              </div>
              <span className="text-xs font-semibold text-[var(--fcc-ink)]">{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pillars() {
  return (
    <section id="features" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="label-overline">Version 1 · what KidHQ does on day one</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)] mt-3 leading-[1.05]">
            Four things, done well — instead of forty done badly.
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {V1_PILLARS.map((f) => (
            <div key={f.num} className="fcc-card p-8 hover:-translate-y-1 transition-transform">
              <div className="flex items-start gap-4">
                <span className="font-display text-3xl text-[var(--fcc-peach)] leading-none">{f.num}</span>
                <div className="w-11 h-11 rounded-2xl bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] flex items-center justify-center">
                  <f.icon className="w-5 h-5" />
                </div>
              </div>
              <h3 className="font-display text-2xl mt-5 text-[var(--fcc-ink)]">{f.title}</h3>
              <p className="text-[var(--fcc-ink-soft)] mt-3 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Timeline() {
  const days = [
    { icon: Sun, label: "Today", items: ["3:30 PM · Emma dentist (bring insurance card)", "Pack orange slices for Saturday game"] },
    { icon: Clock, label: "Tomorrow", items: ["10 AM · Oak Ridge picnic, Riverside Park", "10 AM · Emma U10 soccer — Field 4 (location change)"] },
    { icon: CalendarDays, label: "This week", items: ["Sun · Noah camp form + $85 balance due", "Mon · Noah math test", "Wed · Emma planetarium field trip"] },
  ];
  return (
    <section id="timeline" className="py-24 bg-[var(--fcc-cream-2)] border-y border-[var(--fcc-border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="label-overline">The killer feature · family timeline</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)] mt-3 leading-[1.05]">Parents don't want emails. Parents want answers.</h2>
          <p className="mt-5 text-[var(--fcc-ink-soft)] leading-relaxed text-lg">When you wake up and ask <em className="not-italic font-semibold text-[var(--fcc-ink)]">"What do my kids need today?"</em> — KidHQ should be the answer. Everything else is secondary.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {days.map((d) => (
            <div key={d.label} className="fcc-card p-7">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-[var(--fcc-green)] text-white flex items-center justify-center"><d.icon className="w-4 h-4" /></span>
                <h3 className="font-display text-2xl text-[var(--fcc-ink)]">{d.label}</h3>
              </div>
              <ul className="mt-5 space-y-3 text-sm text-[var(--fcc-ink-soft)]">
                {d.items.map(t => <li key={t} className="flex gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-0.5 shrink-0" /><span>{t}</span></li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductShot() {
  return (
    <section className="py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="label-overline">KidHQ Copilot</p>
          <h3 className="font-display text-3xl sm:text-4xl text-[var(--fcc-ink)] mt-3 leading-[1.1]">
            Ask anything. Get the answer — not a search result.
          </h3>
          <p className="mt-6 text-[var(--fcc-ink-soft)] leading-relaxed">
            Copilot reads your kids' inbox so you don't have to. It answers in seconds and only uses what's actually in your messages — so it never invents a field trip that doesn't exist.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-[var(--fcc-ink-soft)]">
            <li className="flex gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-1" /> "What is due this week?"</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-1" /> "What forms need signing?"</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-1" /> "Any payments due?"</li>
            <li className="flex gap-2"><Check className="w-4 h-4 text-[var(--fcc-green)] mt-1" /> "What does Emma have tomorrow?"</li>
          </ul>
        </div>
        <div className="relative">
          <div className="fcc-card p-6 anim-fade-up">
            <div className="flex items-center gap-2">
              <span className="fcc-pill" style={{ background: "var(--fcc-noah)", color: "var(--fcc-green)" }}>Form</span>
              <span className="fcc-pill bg-[var(--fcc-green-soft)] text-[var(--fcc-green)]">90% match</span>
            </div>
            <p className="font-display text-lg mt-4 text-[var(--fcc-ink)]">Action needed: Emma planetarium permission slip and payment</p>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">Emma needs a signed permission slip and $12 by Saturday for next week's field trip.</p>
            <div className="mt-4 rounded-2xl bg-[var(--fcc-cream-2)] p-4">
              <p className="label-overline">Why it matters</p>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">recognized organization: Oak Ridge Elementary · mentions Emma · classified as Form</p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button className="fcc-btn-primary justify-center text-sm py-2.5">Keep</button>
              <button className="fcc-btn-outline justify-center text-sm py-2.5">Reject</button>
              <button className="text-sm py-2.5 rounded-xl bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] font-semibold">Always include</button>
              <button className="text-sm py-2.5 rounded-xl bg-[var(--fcc-red-soft)] text-[#8B241D] font-semibold">Never include</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <p className="label-overline">Pricing</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)] mt-3">One calm price per family. No per-child fees.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {PRICING.map((p) => (
            <div key={p.name} className={`relative p-8 rounded-3xl border ${p.highlight ? "bg-[var(--fcc-ink)] text-white border-[var(--fcc-ink)]" : "bg-white border-[var(--fcc-border)]"}`}>
              {p.highlight && <span className="absolute -top-3 left-8 fcc-pill" style={{ background: "var(--fcc-peach)", color: "var(--fcc-ink)" }}>Most popular</span>}
              <h3 className="font-display text-2xl">{p.name}</h3>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-5xl">{p.price}</span>
                <span className={`pb-2 text-sm ${p.highlight ? "text-white/70" : "text-[var(--fcc-muted)]"}`}>{p.period}</span>
              </div>
              <ul className={`mt-6 space-y-2 text-sm ${p.highlight ? "text-white/85" : "text-[var(--fcc-ink-soft)]"}`}>
                {p.features.map(f => <li key={f} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
              <a href="#waitlist" data-testid={p.testId} className={`mt-7 w-full inline-flex justify-center px-5 py-3 rounded-xl text-sm font-semibold ${p.highlight ? "bg-white text-[var(--fcc-ink)]" : "fcc-btn-primary"}`}>{p.cta}</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  return (
    <section id="roadmap" className="py-24 bg-[var(--fcc-cream-2)] border-y border-[var(--fcc-border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="label-overline">Roadmap</p>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)] mt-3 leading-[1.05]">Where KidHQ is going next.</h2>
          <p className="mt-5 text-[var(--fcc-ink-soft)] leading-relaxed">Today, KidHQ runs on a demo inbox. Tomorrow, it plugs into the platforms your family already lives on — and eventually, it takes action for you.</p>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ROADMAP.map(r => (
            <div key={r.phase} className={`p-7 rounded-3xl border ${r.highlight ? "bg-[var(--fcc-ink)] text-white border-[var(--fcc-ink)]" : "bg-white border-[var(--fcc-border)]"}`}>
              <span className={`text-xs uppercase tracking-widest font-semibold ${r.highlight ? "text-[var(--fcc-peach)]" : "text-[var(--fcc-green)]"}`}>{r.phase}</span>
              <h3 className={`font-display text-xl mt-2 ${r.highlight ? "text-white" : "text-[var(--fcc-ink)]"}`}>{r.label}</h3>
              <ul className={`mt-4 space-y-1.5 text-sm ${r.highlight ? "text-white/85" : "text-[var(--fcc-ink-soft)]"}`}>
                {r.items.map(i => <li key={i} className="flex gap-2"><span className={`w-1 h-1 rounded-full mt-2 shrink-0 ${r.highlight ? "bg-[var(--fcc-peach)]" : "bg-[var(--fcc-green)]"}`} /> {i}</li>)}
              </ul>
              {r.highlight && <p className="mt-4 text-xs text-[var(--fcc-peach)] font-semibold">The big opportunity.</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Privacy() {
  return (
    <section id="privacy" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="fcc-card-accent p-10 sm:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="w-12 h-12 rounded-2xl bg-[var(--fcc-ink)] text-white flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <h3 className="font-display text-3xl sm:text-4xl text-[var(--fcc-ink)] mt-5">Your kids' details, treated like our own.</h3>
            <p className="text-[var(--fcc-ink-soft)] mt-4 leading-relaxed">Email content is processed over encrypted channels and never used to train any model. We only retain what's needed to power your KidHQ timeline — and you can wipe it in one click. Adult emails (statements, newsletters, marketing) are filtered out before they're ever shown.</p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {["No model training on your data", "Adult emails filtered out", "One-click data wipe", "SOC 2 roadmap in 2026", "Per-sender allow/block list", "Encrypted at rest & in transit"].map(t => (
              <li key={t} className="fcc-card p-4 text-sm font-medium text-[var(--fcc-ink)]">
                <Check className="w-4 h-4 text-[var(--fcc-green)] inline mr-2" />{t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <p className="label-overline text-center">Frequently asked</p>
        <h2 className="font-display text-4xl text-center text-[var(--fcc-ink)] mt-3">Questions parents ask us first.</h2>
        <div className="mt-12 divide-y divide-[var(--fcc-border)] border-y border-[var(--fcc-border)]">
          {FAQS.map((f, i) => (
            <button key={f.q} data-testid={`${LANDING.faqItem}-${i}`} onClick={() => setOpen(open === i ? -1 : i)} className="w-full text-left py-5 group">
              <div className="flex items-start justify-between gap-6">
                <span className="font-display text-lg sm:text-xl text-[var(--fcc-ink)]">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[var(--fcc-ink-soft)] transition-transform ${open === i ? "rotate-180" : ""}`} />
              </div>
              {open === i && <p className="text-[var(--fcc-ink-soft)] mt-3 leading-relaxed text-sm">{f.a}</p>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section id="waitlist" className="py-24 sm:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="fcc-card p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[var(--fcc-peach)] opacity-60 blur-3xl" />
          <div className="relative">
            <p className="label-overline">Join the KidHQ waitlist</p>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--fcc-ink)] mt-3 leading-[1.05]">A calmer week for your family starts here.</h2>
            <p className="text-[var(--fcc-ink-soft)] mt-4">We'll send you an invite when the next cohort opens. No spam, no marketing — only your invite link.</p>
            <WaitlistForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", num_children: 2, biggest_pain_point: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await joinWaitlist(form);
      setDone(true);
      toast.success("You're on the list. Welcome to KidHQ!");
    } catch (err) {
      toast.error("Something went wrong. Try again?");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid={LANDING.waitlistSuccess} className="mt-8 rounded-2xl border border-[var(--fcc-green)] bg-[var(--fcc-green-soft)] p-6 text-[var(--fcc-green)]">
        <p className="font-display text-xl">You're on the list.</p>
        <p className="text-sm mt-2">We'll be in touch when the next cohort opens. Until then, try the demo:</p>
        <Link to="/app" className="fcc-btn-primary mt-4 inline-flex">Open the demo →</Link>
      </div>
    );
  }

  return (
    <form data-testid={LANDING.waitlistForm} onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4">
      <input data-testid={LANDING.waitlistName} required placeholder="Your name" className="fcc-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
      <input data-testid={LANDING.waitlistEmail} required type="email" placeholder="you@email.com" className="fcc-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
      <select data-testid={LANDING.waitlistChildren} className="fcc-input" value={form.num_children} onChange={e => setForm({ ...form, num_children: parseInt(e.target.value) })}>
        {[1,2,3,4,"5+"].map(n => <option key={n} value={typeof n === 'number' ? n : 5}>{n} {n === 1 ? "child" : "children"}</option>)}
      </select>
      <input data-testid={LANDING.waitlistPain} required placeholder="Biggest pain point right now" className="fcc-input" value={form.biggest_pain_point} onChange={e => setForm({ ...form, biggest_pain_point: e.target.value })} />
      <button data-testid={LANDING.waitlistSubmit} disabled={loading} className="fcc-btn-primary sm:col-span-2 justify-center">
        {loading ? "Adding you…" : "Reserve my spot"} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
}

function Footer() {
  return (
    <footer className="py-12 border-t border-[var(--fcc-border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 gap-6 items-start text-sm text-[var(--fcc-ink-soft)]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[var(--fcc-ink)] text-white flex items-center justify-center relative shrink-0">
            <Heart className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--fcc-orange)] rounded-full" />
          </div>
          <div>
            <div className="font-display text-[var(--fcc-ink)] text-lg">KidHQ</div>
            <p className="mt-1 max-w-md leading-relaxed">The AI operating system for parents. We turn school, sports, camp, and activity emails into tasks, deadlines, reminders, and schedules — so you never miss what matters.</p>
          </div>
        </div>
        <div className="sm:text-right text-xs text-[var(--fcc-muted)] space-y-1">
          <div>kidhq.net · A product of <strong className="text-[var(--fcc-ink-soft)]">All IT Solutions LLC</strong></div>
          <div className="italic">Smart people, smart solutions</div>
          <div>© All IT Solutions LLC · Built with Claude Sonnet 4.5</div>
        </div>
      </div>
    </footer>
  );
}
